import {
  useShareAddMemberByIdentifierMutation,
  ShareAddMemberWithPermissionsMutationVariables,
  ShareAddMemberByIdentifierMutationVariables,
  GetShareMembershipsQueryVariables,
  GetShareMembershipsDocument,
  ShareAddMemberByIdentifierMutation,
} from 'gqlTypes'
import { useCallback, useState } from 'react'
import { ErrorHandler } from 'modules/tracing'
import { useToast } from 'containers/ToastManager'
import { useModalContext } from 'containers/ModalManager/ModalContext'

type Permissions = ShareAddMemberWithPermissionsMutationVariables['permissions']

const enableCommentsIfEditPermissions = (permissions: Permissions) =>
  permissions.privateAccessLevel === 'EDIT' || permissions.commentsEnabled

export const useAddMemberByIdentifier = () => {
  const { showToast } = useToast()
  const { hideModal } = useModalContext()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [variables, setVariables] = useState<
    ShareAddMemberByIdentifierMutationVariables | undefined
  >(undefined)

  const reset = useCallback(() => {
    setDialogOpen(false)
    setVariables(undefined)
  }, [])

  const [
    addMemberByIdentifier,
    { loading },
  ] = useShareAddMemberByIdentifierMutation({
    onCompleted(data) {
      reset()

      if (!data?.shareAddMemberByIdentifier) {
        ErrorHandler.shouldNeverHappen(
          'Share add member permissions mutation should return valid data'
        )
        return
      }

      const { shareAddMemberByIdentifier } = data

      if (shareAddMemberByIdentifier?.share?.userCanUpdateSettings === false) {
        hideModal()
        showToast("You can no longer view this document's settings")
        return
      }

      showToast(
        `Invitation sent to ${
          shareAddMemberByIdentifier.workspaceMembership?.user?.name ||
          shareAddMemberByIdentifier.email
        }`
      )
    },
    onError(error) {
      if (error.includesErrorCode('CONFIRMATION_REQUIRED')) {
        setDialogOpen(true)
        return
      }

      const { graphqlErrorMessages, message } = error

      // Show this only if GraphQL (business) error happened
      if (graphqlErrorMessages.length > 0) {
        showToast(message, 'negative')
        return
      }

      reset()

      // Show this if network error occurred
      // NOTE: this GraphQL / network error handling division might be unnecessary
      //       and whoever will work on this source code again, can investigate it
      //       and simplify it. Or explain better why the division is necessary.
      showToast(
        'There was a problem inviting the member. Please try again.',
        'negative'
      )
    },
    awaitRefetchQueries: true,
    refetchQueries: ({ data }) => {
      const typedData = data as ShareAddMemberByIdentifierMutation

      const variables: GetShareMembershipsQueryVariables = {
        shareIdentifier: typedData.shareAddMemberByIdentifier!.share!
          .identifier,
      }

      return [{ query: GetShareMembershipsDocument, variables }]
    },
  })

  const addMemberByAutocomplete = useCallback(
    (
      variables: ShareAddMemberByIdentifierMutationVariables,
      confirmed: boolean = false
    ) => {
      setVariables(variables)
      return addMemberByIdentifier({
        variables: {
          ...variables,
          permissions: {
            ...variables.permissions,
            commentsEnabled: enableCommentsIfEditPermissions(
              variables.permissions
            ),
            confirmed,
          },
        },
      })
    },
    [addMemberByIdentifier]
  )

  const getAddMemberByAutocompleteConfirmDialogProps = useCallback(
    () => ({
      hideModal() {
        reset()
      },
      onConfirm() {
        if (variables) addMemberByAutocomplete(variables, true)
      },
      isModalOpen: true,
      confirmButton: {
        text: 'Confirm',
        intent: 'positive',
        loading,
      } as const,
    }),
    [addMemberByAutocomplete, loading, variables, reset]
  )

  return {
    userIdentifier: variables?.userIdentifier,
    addMemberByAutocomplete,
    addMemberByAutocompleteLoading: loading,
    addMemberByAutocompleteConfirmDialogOpen: dialogOpen,
    getAddMemberByAutocompleteConfirmDialogProps,
  }
}
